import PolarisIcon from '@/components/_common/PolarisIcon';
import { selectFormIsDraft, selectFormIsIncomplete, selectSchemaFormRefs } from '@/redux/selectors/orderPlacementSelectors';
import { toggleFormIsIncomplete } from '@/redux/slices/orderPlacementSlice';
import { Button, Card, CardContent, CardHeader, Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import OPOrderStepperButtons from '../OPOrderStepperButtons';
import OPOrderDetails from './OPOrderDetails';
import ProfileDetails from './ProfileDetails/ProfileDetails';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { FormValues, SchemaFormRefSource } from '@/redux/slices/slice_types';

const OPStep1OrderDetails = () => {
  const dispatch = useAppDispatch();
  const { dirty, values, setValues, initialValues, setFieldTouched } = useFormikContext<FormValues>();
  const formIsIncomplete = useAppSelector(selectFormIsIncomplete);
  const formIsDraft = useAppSelector(selectFormIsDraft);
  // disable if any jobs have errors or if the workflow type is version_based or if the last job has no customer or profile
  const lastJobIsEmpty = !values?.jobs[values?.jobs?.length - 1]?.customer || !values?.jobs[values?.jobs?.length - 1]?.profile;
  const workflow_type = values.jobs?.[0]?.profile?.data?.workflow_type || 'version_based';
  const isVersionBased = ['version_based', 'package', 'version_based_bundle'].indexOf(workflow_type) !== -1;
  const [showAddNew, setShowAddNew] = useState(values?.jobs?.length > 0 && isVersionBased && !lastJobIsEmpty);
  const showSaveAndAdd = isVersionBased && !showAddNew && values.jobs?.[values.jobs?.length - 1 || 0]?.profile?.data;
  const formRefs = useAppSelector(selectSchemaFormRefs);

  const onValidateFormRefs = () => {
    const step1AdditionalSchemaForm = formRefs.get(SchemaFormRefSource.PROFILE_DETAIL_INFO);
    const step1AdditionalSpecSchemaForm = formRefs.get(SchemaFormRefSource.PROFILE_SPEC_INFO);
    return (
      (step1AdditionalSchemaForm &&
        step1AdditionalSchemaForm?.current &&
        step1AdditionalSchemaForm?.current?.formElement.current &&
        !step1AdditionalSchemaForm.current.validateForm()) ||
      (step1AdditionalSpecSchemaForm &&
        step1AdditionalSpecSchemaForm?.current &&
        step1AdditionalSpecSchemaForm?.current?.formElement.current &&
        !step1AdditionalSpecSchemaForm.current.validateForm())
    );
  };

  useEffect(() => {
    if (values?.jobs?.length === 0) {
      setValues({
        ...values,
        jobs: [initialValues.jobs?.[0]]
      });
      setFieldTouched(`jobs[${values.jobs.length - 1}]`, false, true);
      setShowAddNew(false);
    }
  }, [values?.jobs]);

  useEffect(() => {
    if (dirty && !formIsIncomplete && !formIsDraft) {
      dispatch(toggleFormIsIncomplete(true));
    }
  }, [dirty]);

  const addNewProfile = () => {
    setShowAddNew(false);
    const lastJob = values.jobs[values.jobs.length - 1];

    setValues({
      ...values,
      jobs: [
        ...values.jobs,
        {
          ...initialValues.jobs?.[0],
          customer: lastJob?.customer
        }
      ]
    });
    setFieldTouched(`jobs[${values.jobs.length - 1}]`, false, true);
  };

  return (
    <>
      <OrderDetailsCard>
        <CardHeader title="Order Details" />
        <CardContent>
          <OPOrderDetails />
        </CardContent>
      </OrderDetailsCard>

      <ProfileDetails lastIsOpen={!showAddNew} />

      {showSaveAndAdd && (
        <Button variant="outlined" color="teal" sx={{ mt: 3 }} startIcon={<PolarisIcon icon="add" height={16} />} onClick={() => setShowAddNew(true)}>
          Save
        </Button>
      )}

      {showSaveAndAdd && (
        <Button variant="outlined" sx={{ mt: 3, ml: 2 }} startIcon={<PolarisIcon icon="add" height={16} />} onClick={addNewProfile}>
          Save & Add New
        </Button>
      )}

      {showAddNew && (
        <Button variant="outlined" sx={{ mt: 3 }} startIcon={<PolarisIcon icon="add" height={16} />} onClick={addNewProfile}>
          Add New
        </Button>
      )}

      <Grid container>
        <Grid item sx={{ ml: 'auto', mt: 5 }}>
          <OPOrderStepperButtons onPerformValidations={onValidateFormRefs} />
        </Grid>
      </Grid>
    </>
  );
};

export default OPStep1OrderDetails;

const OrderDetailsCard = styled(Card)`
  padding: 10px;
  padding-bottom: 40px;
  margin-bottom: 30px;
`;
